/**
 * @fileoverview Single source of truth for access-token persistence in the
 * admin web client.
 *
 * Why this exists
 * ---------------
 * Storing the access token in `localStorage` keeps it alive across browser
 * sessions — an attacker who lands an XSS payload on the page steals a
 * credential that is good for as long as the user keeps using this browser.
 * sessionStorage shrinks that window to "this tab" — close the tab, the
 * token is gone — and we keep an in-memory copy so React state stays
 * authoritative within a session.
 *
 * What about the refresh token?
 * -----------------------------
 * It is NEVER stored here. The backend ships the refresh token to the
 * browser as an `httpOnly` cookie (`config/refreshCookie.ts` in the API).
 * JavaScript cannot read or write that cookie — `document.cookie` simply
 * doesn't see it. Every call to `POST /api/auth/refresh` is sent with
 * `withCredentials: true`, the browser attaches the cookie automatically,
 * and the API returns a fresh access token in the response body. The web
 * client never touches the refresh token in any JS-accessible storage.
 *
 * Backward compatibility
 * ----------------------
 * Older builds wrote the access token to `localStorage['token']` and the
 * refresh token to `localStorage['refresh_token']`. On module import we
 * delete both so a returning user with a legacy install doesn't carry the
 * weak storage forward.
 *
 * Kept under 100 lines so the auth surface is auditable at a glance.
 */

const ACCESS_TOKEN_STORAGE_KEY = 'protea_access_token';

// Legacy keys carried over from the localStorage era. Cleared on first load.
const LEGACY_ACCESS_TOKEN_KEY = 'token';
const LEGACY_REFRESH_TOKEN_KEY = 'refresh_token';

let inMemoryAccessToken: string | null = null;

function safeStorage(): Storage | null {
  try {
    if (typeof window === 'undefined') return null;
    return window.sessionStorage;
  } catch {
    // Private browsing mode in some browsers throws on sessionStorage access.
    return null;
  }
}

/**
 * Wipe credentials written by older builds so an upgrade doesn't leave a
 * long-lived token in localStorage. Called once at module-import time.
 */
function clearLegacyLocalStorageCredentials(): void {
  try {
    if (typeof window === 'undefined') return;
    window.localStorage.removeItem(LEGACY_ACCESS_TOKEN_KEY);
    window.localStorage.removeItem(LEGACY_REFRESH_TOKEN_KEY);
  } catch {
    // Best-effort cleanup; never throw from module init.
  }
}

// Hydrate the in-memory cache from sessionStorage so a hard refresh inside
// the same tab keeps the user logged in without forcing a round-trip to
// the refresh endpoint.
inMemoryAccessToken = safeStorage()?.getItem(ACCESS_TOKEN_STORAGE_KEY) ?? null;
clearLegacyLocalStorageCredentials();

export function getAccessToken(): string | null {
  return inMemoryAccessToken;
}

export function setAccessToken(token: string): void {
  inMemoryAccessToken = token;
  try {
    safeStorage()?.setItem(ACCESS_TOKEN_STORAGE_KEY, token);
  } catch {
    // Disk-full / quota errors are non-fatal — the in-memory copy still
    // serves the rest of the session.
  }
}

export function clearAccessToken(): void {
  inMemoryAccessToken = null;
  try {
    safeStorage()?.removeItem(ACCESS_TOKEN_STORAGE_KEY);
  } catch {
    // Same rationale as setAccessToken.
  }
}
